import React, { useEffect, useState, useCallback } from 'react';
import Joyride, { Step, CallBackProps, STATUS, ACTIONS } from 'react-joyride';

interface SingleStepSpotlightProps {
  target: string;
  content: React.ReactNode;
  run: boolean;
  onClose: () => void;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  offset?: number;
  disableScrolling?: boolean;
}

export const SingleStepSpotlight: React.FC<SingleStepSpotlightProps> = ({
  target,
  content,
  run,
  onClose,
  placement = 'right',
  offset = 10,
  disableScrolling = false,
}) => {
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    if (run) {
      // Small delay to ensure the target element is rendered
      const timer = setTimeout(() => {
        setIsRunning(true);
      }, 300);
      return () => clearTimeout(timer);
    }
    setIsRunning(false);
    return undefined;
  }, [run]);

  const handleCallback = useCallback(
    (data: CallBackProps) => {
      const { status, action } = data;

      // Close when user clicks the close button or outside
      if (
        status === STATUS.FINISHED ||
        status === STATUS.SKIPPED ||
        action === ACTIONS.CLOSE
      ) {
        setIsRunning(false);
        onClose();
      }
    },
    [onClose]
  );

  const steps: Step[] = [
    {
      target,
      content,
      placement,
      disableBeacon: true,
      spotlightClicks: true,
    },
  ];

  return (
    <Joyride
      steps={steps}
      run={isRunning}
      continuous={false}
      showProgress={false}
      showSkipButton={false}
      disableOverlayClose={false}
      spotlightPadding={12}
      scrollOffset={offset}
      disableScrolling={disableScrolling}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: 'hsl(var(--primary))',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 8,
          padding: 0,
          fontSize: 13,
          backgroundColor: '#ffffff',
          color: '#1a1a1a',
          boxShadow:
            '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(0, 0, 0, 0.05)',
          maxWidth: 320,
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipContent: {
          padding: '14px 16px 10px 16px',
          lineHeight: '1.5',
        },
        buttonNext: {
          backgroundColor: 'hsl(var(--primary))',
          color: '#ffffff',
          borderRadius: 6,
          padding: '8px 16px',
          fontSize: 13,
          fontWeight: 500,
          margin: '0 16px 14px 16px',
          transition: 'all 0.2s ease',
        },
        buttonClose: {
          display: 'none',
        },
      }}
      locale={{
        close: 'Got it!',
        last: 'Got it!',
      }}
      floaterProps={{
        styles: {
          arrow: {
            length: 8,
            spread: 12,
          },
        },
      }}
    />
  );
};
